import { motion, useReducedMotion, type Variants } from 'motion/react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { MARKETING_EVENTS } from '@/lib/analytics';
import { getAttributionProps } from '@/lib/attribution';
import { CtaButton } from './cta-button';
import { scrollToSection } from './scroll-to-section';

// TODO(#203): PLACEHOLDER SOCIAL PROOF — these are stock illustrations, not
// customers, and "Hundreds of Happy Customers worldwide" is a headline claim
// the product cannot yet evidence. Both must be replaced with real, verifiable
// proof before this page goes in front of customers.
// The illustrations are transparent outside the face, so each gets its own tint
// behind it to break up the row — terracotta, then a green/sky/cyan spread that
// sits with the landscape behind it. All four are registered tokens at low
// alpha, so they read as tints over paper and over the dark surface alike (the
// faces themselves are white, so they stay legible on either).
//
// Fixed per avatar rather than randomised at render: Math.random() here would
// flicker on hydration and diverge from the prerendered HTML.
const PROOF_AVATARS = [
  { src: '/avatars/avatar-1.svg', tint: 'bg-ring/25' },
  { src: '/avatars/avatar-2.svg', tint: 'bg-color-5/30' },
  { src: '/avatars/avatar-3.svg', tint: 'bg-color-3/25' },
  { src: '/avatars/avatar-4.svg', tint: 'bg-color-4/30' },
];

/** Where the second CTA lands. */
const SAMPLE_REPORT_HREF = '#sample-report';

// Staggered entrance, adapted from the hero-34 block: support row first, then
// the headline rising out of a blur, then body + CTAs.
const supportVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', damping: 24, stiffness: 100, delay: 0.2 },
  },
};

const titleContainerVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.18, delayChildren: 0.3 } },
};

const titleLineVariants: Variants = {
  hidden: { opacity: 0, y: 40, filter: 'blur(12px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { type: 'spring', damping: 30, stiffness: 90, mass: 1.2 },
  },
};

const bodyContainerVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.13, delayChildren: 0.85 } },
};

const bodyItemVariants: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { type: 'spring', damping: 22, stiffness: 110 },
  },
};

/**
 * Single-column editorial hero on the hero-34 skeleton: social proof → headline
 * → body + dual CTA, left-aligned on a full-height band over the block's
 * landscape photograph. The sample report that used to sit in the right-hand
 * column now lives in its own <SampleReport> section, which the second CTA
 * scrolls to.
 */
export function Hero() {
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();

  // Reduced motion: render the same tree with animation disabled rather than
  // forking the markup, so the two paths can't drift apart.
  const animate = reduceMotion ? undefined : 'show';
  const initial = reduceMotion ? undefined : 'hidden';

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, label: string) => {
    posthog.capture(MARKETING_EVENTS.navClicked, {
      label,
      href: SAMPLE_REPORT_HREF,
      location: 'hero',
      ...getAttributionProps(),
    });
    scrollToSection(e, SAMPLE_REPORT_HREF);
  };

  return (
    // The height is the masthead's (110px, 126px from sm) subtracted from the
    // viewport, so the hero + masthead fill exactly one screen and the trust
    // strip always starts below the fold. <HeroBackdrop> covers the same band.
    // Phones sit the column a quarter of the way down the band rather than at
    // the top: top-aligning left a dead gap between the CTA and the fold, and
    // true centring pushed the headline too low. The two flex spacers split the
    // leftover height 1:3, which keeps the offset proportional at any phone
    // height. `sm:block` hands the band straight back to the original
    // top-aligned flow, so nothing above 640px moves.
    <section className="flex min-h-[calc(100svh-110px)] flex-col sm:block sm:min-h-[calc(100svh-126px)]">
      <div aria-hidden="true" className="flex-1 sm:hidden" />
      <div className="mx-auto w-full max-w-6xl px-4 pb-14 pt-8 sm:px-6 sm:pt-12">
        <div className="flex max-w-4xl flex-col items-start">
          <motion.div
            variants={supportVariants}
            initial={initial}
            animate={animate}
            className="mb-8 flex items-center gap-4"
          >
            {/* shrink-0: without it the wrapping proof line squeezes the avatar
                row and the two overlap on a 375px screen. */}
            <div className="flex shrink-0 -space-x-2.5">
              {PROOF_AVATARS.map(({ src, tint }) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  aria-hidden="true"
                  width={32}
                  height={32}
                  /* The separating ring has to stay light in both themes. Plain
                     `border-background` goes black in dark mode, which cut the
                     row into hard black slices instead of reading as overlap. */
                  className={`size-8 rounded-full border-2 border-background object-cover ring-1 ring-foreground/10 dark:border-foreground/25 dark:ring-foreground/20 ${tint}`}
                />
              ))}
            </div>
            <p className="text-sm text-foreground/65">100s of Happy Customers worldwide</p>
          </motion.div>

          {/* font-normal, not font-medium: Fraunces sets heavier than Lora did at
            the same weight, so display sizes step down one notch to stay calm. */}
          <motion.h1
            variants={titleContainerVariants}
            initial={initial}
            animate={animate}
            className="type-wonk font-display text-4xl font-normal leading-[1.12] tracking-tight text-foreground [overflow-wrap:anywhere] sm:text-5xl md:text-[3.4rem]"
          >
            <motion.span variants={titleLineVariants} className="block">
              Your Plot,
            </motion.span>
            <motion.span variants={titleLineVariants} className="block">
              <span className="border-b-2 border-ring/70 pb-0.5 text-ring">Monitored</span> Monthly.
            </motion.span>
          </motion.h1>

          <motion.div
            variants={bodyContainerVariants}
            initial={initial}
            animate={animate}
            className="flex flex-col items-start"
          >
            <motion.p
              variants={bodyItemVariants}
              className="mt-5 max-w-md text-lg leading-relaxed text-foreground/65 sm:mt-7"
            >
              Protect your land in India with a reliable, visual update subscription.
            </motion.p>

            <motion.div
              variants={bodyItemVariants}
              className="mt-7 flex w-full flex-col items-start gap-3 sm:mt-9 sm:w-auto sm:flex-row sm:items-center sm:gap-6"
            >
              {/* Phones get the compact pair: the full-width pill plus a
                  three-word secondary label ate the fold on a 375px screen. */}
              <CtaButton location="hero" variant="clay" depth="3d" className="self-start">
                Start Monitoring
                <ArrowRight className="size-4" />
              </CtaButton>

              <a
                href={SAMPLE_REPORT_HREF}
                onClick={(e) => handleAnchorClick(e, 'Sample Report')}
                /* Hidden on phones: the fold is tight there and the sample
                   report is the next thing down the page anyway. */
                className="group hidden h-10 items-center gap-2 self-start rounded-md text-sm font-medium text-foreground transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:flex sm:h-12 sm:gap-3 sm:px-2 sm:text-base"
              >
                Sample Report
                <span className="flex size-7 items-center justify-center rounded-full bg-foreground text-background transition-transform group-hover:translate-y-0.5 sm:size-8">
                  <ArrowDown className="size-4" />
                </span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <div aria-hidden="true" className="flex-[3] sm:hidden" />
    </section>
  );
}
